import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { fetchEffectiveModulePermissions } from '../utils/userModulePermissions';
import {
  canPrintCashClose,
  canViewCashMovements,
  canViewFullCashClose,
  canViewRecentCashMovements,
  canVoidCashMovements,
  resolveRecentMovementsLimit,
} from '../utils/cashCloseVisibility';

const SUPERADMIN_EMAIL = 'adpenia@gmail.com';

// Caja y CajaDetalle montan este hook por separado, y navegar entre ellas
// repetiria las dos queries de permisos cada vez. Se cachea por
// organizacion+usuario+rol durante una ventana corta: alcanza para el ida y
// vuelta, y un cambio de permisos se refleja al minuto (o al recargar).
const PERMISSIONS_CACHE_TTL_MS = 60_000;
const permissionsCache = new Map();

const buildCacheKey = (organizationId, userId, roleName) =>
  `${organizationId}|${userId}|${String(roleName || '').trim().toLowerCase()}`;

const readCachedModules = (key) => {
  const entry = permissionsCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.at > PERMISSIONS_CACHE_TTL_MS) {
    permissionsCache.delete(key);
    return null;
  }
  return entry.modules;
};

/**
 * Resuelve si el usuario logueado puede ver el cierre completo de caja
 * (historial interno, detalle del arqueo y teorico/diferencia impresos).
 *
 * Vive en un hook propio y no en `Sistema.jsx` a proposito: Sistema.jsx esta en
 * la capa protegida del sync a Gastify, asi que un cambio ahi no viaja. Este
 * hook y `utils/cashCloseVisibility.js` si viajan, y Caja/CajaDetalle los
 * consumen directo.
 *
 * `resolving` arranca en true cuando el cierre ciego esta activo: la pantalla
 * debe esperar antes de pintar el historial, porque un flash de la diferencia
 * ya rompe el arqueo ciego.
 */
export function useCashCloseAccess() {
  const { profile, organization, user } = useAuth();

  const blindCashCloseEnabled = Boolean(organization?.blind_cash_close);
  const isSuperAdmin = String(user?.email || '').toLowerCase() === SUPERADMIN_EMAIL;

  // `organization` todavia sin cargar es indistinguible de "flag apagado", y el
  // default de `blindCashCloseEnabled` seria false => conceder. Marcamos ese
  // estado como "resolviendo" para fallar cerrado mientras tanto. Importa sobre
  // todo en Gastify, donde AuthContext.jsx es PROTECTED y podria no exponer
  // `organization` igual que Impulza.
  const organizationPending = Boolean(profile?.organization_id) && !organization;

  const [effectiveModules, setEffectiveModules] = useState(null);
  const [resolving, setResolving] = useState(blindCashCloseEnabled || organizationPending);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (organizationPending) {
      setResolving(true);
      return undefined;
    }

    // Sin cierre ciego no hace falta la consulta: `canViewFullCashClose` ya
    // devuelve true y evitamos un round-trip por cada visita a Caja.
    if (!blindCashCloseEnabled || isSuperAdmin) {
      setResolving(false);
      return undefined;
    }

    if (!profile?.organization_id || !profile?.id) {
      setEffectiveModules(null);
      setResolving(false);
      return undefined;
    }

    const cacheKey = buildCacheKey(profile.organization_id, profile.id, profile.role);
    const cached = readCachedModules(cacheKey);
    if (cached) {
      setEffectiveModules(cached);
      setResolving(false);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    let cancelled = false;

    setResolving(true);

    (async () => {
      try {
        const { effective } = await fetchEffectiveModulePermissions({
          supabase,
          organizationId: profile.organization_id,
          userId: profile.id,
          roleName: profile.role,
        });
        permissionsCache.set(cacheKey, { modules: effective, at: Date.now() });
        if (cancelled || requestIdRef.current !== requestId) return;
        setEffectiveModules(effective);
      } catch (error) {
        console.error('No se pudieron resolver los permisos de cierre de caja:', error);
        if (cancelled || requestIdRef.current !== requestId) return;
        // Fallar cerrado: si no sabemos que permisos tiene, no le mostramos el
        // cierre completo.
        setEffectiveModules(null);
      } finally {
        if (!cancelled && requestIdRef.current === requestId) setResolving(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    blindCashCloseEnabled,
    isSuperAdmin,
    organizationPending,
    profile?.organization_id,
    profile?.id,
    profile?.role,
  ]);

  // Un solo objeto con todas las facultades: Caja.jsx necesita varias a la vez y
  // recalcularlas por separado repetiria el mismo chequeo cuatro veces.
  const cashAccess = useMemo(() => {
    // Mientras falta contexto no se concede nada: el util solo ve el flag y no
    // sabe que la organizacion todavia no llego.
    if (organizationPending) {
      return {
        canViewFullClose: false,
        canViewMovements: false,
        canViewRecentMovements: false,
        canPrintClose: false,
        canVoidMovements: false,
      };
    }

    const params = { blindCashCloseEnabled, profile, isSuperAdmin, effectiveModules };

    return {
      canViewFullClose: canViewFullCashClose(params),
      canViewMovements: canViewCashMovements(params),
      canViewRecentMovements: canViewRecentCashMovements(params),
      canPrintClose: canPrintCashClose(params),
      canVoidMovements: canVoidCashMovements(params),
    };
  }, [organizationPending, blindCashCloseEnabled, profile, isSuperAdmin, effectiveModules]);

  // Cuantos movimientos ve quien solo tiene el permiso acotado. Lo define la
  // organizacion (`blind_cash_recent_movements`); si la columna no existe
  // todavia en esta base, el util cae al default.
  const recentMovementsLimit = resolveRecentMovementsLimit(
    organization?.blind_cash_recent_movements,
  );

  return {
    blindCashCloseEnabled,
    ...cashAccess,
    recentMovementsLimit,
    resolvingCashCloseAccess: resolving,
    // Lo expone el hook para que las pantallas no repitan el email del soporte
    // (el control de arqueos tambien necesita distinguirlo).
    isSuperAdmin,
  };
}

export default useCashCloseAccess;
